import { alpha, Box, Grid, Slide, Typography, useTheme } from '@mui/material';

import { useBreakpoint } from '../../hooks/useBreakpoint/useBreakpoint';
import { useSlideshow } from '../../hooks/useSlideshow/useSlideshow';
import { BackButton } from '../BackButton/BackButton';
import { ImageCarouselControl } from '../ImageCarouselControl/ImageCarouselControl';
import {
  ImageCarouselMediaItem,
  type ImageCarouselMediaItemProps,
} from '../ImageCarouselMediaItem/ImageCarouselMediaItem';
import { ImageCarouselSidebar } from '../ImageCarouselSidebar/ImageCarouselSidebar';

export interface ImageCarouselSidebarProps {
  label: string;
  children?: React.ReactNode;
}

export interface ImageCarouselProps {
  photoEvidence: string[];
  showActions?: boolean;
  selectedMedia?: string;
  showImageCounterOverlay?: boolean;
  showImageCounter?: boolean;
  showBackButton?: boolean;
  handleBackButtonClick?: () => void;
  handleNavigate?: (src: string) => void;
  slotProps?: {
    mediaItem?: Omit<ImageCarouselMediaItemProps, 'media'>;
    mediaItemSlot?: React.ReactNode;
    sidebar?: ImageCarouselSidebarProps;
  };
}

export const ImageCarousel = ({
  photoEvidence,
  showActions,
  selectedMedia,
  showImageCounterOverlay,
  showImageCounter,
  showBackButton,
  handleBackButtonClick,
  handleNavigate,
  slotProps,
}: ImageCarouselProps) => {
  const theme = useTheme();
  const maxImages = photoEvidence.length;
  const { isMobile } = useBreakpoint();
  const showSidebar = Boolean(!isMobile && slotProps?.sidebar);
  const showLegendTop = Boolean(isMobile && slotProps?.sidebar);

  const { currentImage, next, previous, selectedIndex } = useSlideshow(
    selectedMedia || photoEvidence[0],
    photoEvidence,
    handleNavigate
  );

  return (
    <Grid container height="100%">
      <Grid item xs={12} sm={showSidebar ? 8 : 12} height="100%">
        <Box position="relative" display="flex" flexDirection="column" height="100dvh">
          {showBackButton ? (
            <Box padding={2}>
              <BackButton onClick={handleBackButtonClick} size="large">
                Back
              </BackButton>
            </Box>
          ) : null}

          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="center"
            alignSelf="center"
            width="100%"
            mb={2}
            sx={{
              touchAction: 'manipulation',
              userSelect: 'none',
            }}
          >
            {slotProps?.mediaItemSlot || <ImageCarouselMediaItem media={currentImage} {...slotProps?.mediaItem} />}
          </Box>

          <Box sx={{ visibility: showLegendTop ? 'visible' : 'hidden' }}>
            {showLegendTop ? <Box>{slotProps?.sidebar?.children}</Box> : null}
          </Box>

          {showImageCounterOverlay ? (
            <Box
              bgcolor={`${theme.palette.neutral?.main}66`}
              borderRadius={2}
              position="absolute"
              bottom={0}
              right={0}
              padding={4}
            >
              <Typography fontWeight="bold" color="white">
                {selectedIndex + 1} / {maxImages}
              </Typography>
            </Box>
          ) : null}

          {showImageCounter ? (
            <Box
              width="100%"
              padding={2}
              display="flex"
              alignItems="center"
              alignSelf="center"
              justifyContent="center"
              gap={2}
              position="fixed"
              bottom={0}
              zIndex={theme.zIndex.appBar}
              sx={{
                backdropFilter: 'blur(4px)',
                backgroundColor: (theme) => alpha(theme.palette.background.default, 0.7),
                borderRadius: 2,
              }}
            >
              {showActions ? <ImageCarouselControl onClick={previous} direction="left" /> : null}
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography fontWeight="bold">{selectedIndex + 1}</Typography> out of
                <Typography fontWeight="bold">{maxImages}</Typography>
              </Box>
              {showActions ? <ImageCarouselControl onClick={next} direction="right" /> : null}
            </Box>
          ) : null}
        </Box>
      </Grid>

      <Grid
        item
        xs={0}
        sm={4}
        sx={{
          backgroundColor: theme.palette.background.default,
        }}
      >
        {showSidebar ? (
          <Slide
            direction="left"
            in={showSidebar}
            mountOnEnter
            unmountOnExit
            timeout={{
              enter: 300,
              exit: 300,
            }}
          >
            <div>
              <ImageCarouselSidebar label={String(slotProps?.sidebar?.label || '')}>
                {slotProps?.sidebar?.children}
              </ImageCarouselSidebar>
            </div>
          </Slide>
        ) : null}
      </Grid>
    </Grid>
  );
};
